import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { TAX_PAYER_TYPE } from '@/store/profile';
import { updateTaxInformation } from '@/store/profile/thunks';
import { showError, showSuccess } from '@/utils';
import {
	requiresTaxId,
	requiresVat,
	validateVat,
	validateVatPrefix
} from '@/utils/billing';

export const useTypeOptions = () => {
	const { t } = useTranslation();
	return [
		{
			value: TAX_PAYER_TYPE.COMPANY,
			label: t(`common.${TAX_PAYER_TYPE.COMPANY}`)
		},
		{
			value: TAX_PAYER_TYPE.PRIVATE,
			label: t(`common.${TAX_PAYER_TYPE.PRIVATE}`)
		}
	];
};

export const useTaxInformationYupForm = ({ onSuccess }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const user = useSelector(state => state.profile.user);
	const taxInformation = useSelector(
		state => state.profile.user?.taxInformation || {}
	);

	const schema = Yup.object({
		type: Yup.string().required(),
		companyName: Yup.string().when('type', {
			is: typeValue => typeValue === TAX_PAYER_TYPE.COMPANY,
			then: currentSchema => currentSchema.required(),
			otherwise: currentSchema => currentSchema.nullable()
		}),
		country: Yup.string().required(),
		vatNumber: Yup.string().when(
			['country', 'type'],
			([country, type], currentSchema) => {
				if (
					!requiresVat({
						country,
						type
					})
				) {
					return currentSchema.nullable();
				}

				return currentSchema
					.test({
						name: 'vatNumberPrefix',
						test: validateVatPrefix(t)
					})
					.test({
						name: 'vatNumber',
						test: validateVat(t)
					})
					.required();
			}
		),
		taxId: Yup.string().when('country', {
			is: countryValue => requiresTaxId(countryValue),
			then: currentSchema => currentSchema.required(),
			otherwise: currentSchema => currentSchema.nullable()
		}),
		firstName: Yup.string().required(),
		lastName: Yup.string().required(),
		address: Yup.string().required(),
		zipCode: Yup.string().required(),
		city: Yup.string().required(),
		email: Yup.string().email().required()
	});

	const methods = useForm({
		resolver: yupResolver(schema),
		mode: 'onChange',
		defaultValues: {
			type: taxInformation.type || TAX_PAYER_TYPE.COMPANY,
			companyName: taxInformation.companyName || '',
			country: taxInformation.country || user.country || '',
			vatNumber: taxInformation.vatNumber || '',
			taxId: taxInformation.taxId || '',
			firstName: taxInformation.firstName || '',
			lastName: taxInformation.lastName || '',
			address: taxInformation.address || '',
			zipCode: taxInformation.zipCode || '',
			city: taxInformation.city || '',
			email: taxInformation.email || user.email || ''
		}
	});

	const typeValue = methods.watch('type');
	const countryValue = methods.watch('country');
	const vatNumberValue = methods.watch('vatNumber');

	useEffect(() => {
		methods.trigger(['vatNumber']);
	}, [vatNumberValue, countryValue]);

	useLayoutEffect(() => {
		if (typeValue === TAX_PAYER_TYPE.PRIVATE) {
			methods.setValue('companyName', '');
		}
	}, [typeValue]);

	const onSubmit = useCallback(async function (values) {
		const taxInfo = { ...values };
		setLoading(true);
		if (requiresVat({ country: taxInfo.country, type: taxInfo.type })) {
			taxInfo.taxId = null;
		} else {
			taxInfo.vatNumber = null;
		}
		dispatch(updateTaxInformation(taxInfo))
			.unwrap()
			.then(() => {
				onSuccess();
				setLoading(false);
				showSuccess(t('account.toast'));
			})
			.catch(error => {
				showError(error);
				setLoading(false);
			});
	}, []);

	return {
		methods,
		loading,
		onSubmit,
		typeValue,
		countryValue
	};
};
